import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { apiGet, apiSend } from "../../../lib/api.js";

/**
 * B-GTCC-A14 / A-GTCC-CHECKS1 — the data layer behind the People page
 * (register rows 23 and 24, tickets A-GTCC-9 / A-GTCC-10).
 *
 * These deliberately do NOT reuse `apps/admin/src/members/useMembers.ts` or
 * `apps/admin/src/invitations/useInvitations.ts`. Those modules are the gtcc1
 * surfaces and were read, not touched: they carry their own cache keys, their
 * own tenant switch (`tenantId ? platform route : tenant route`) and their own
 * hard-delete path. The gtcc console keeps its own keys so an invalidation on
 * one surface cannot silently reshuffle the other — and it invalidates the
 * gtcc1 key alongside its own, the way `GtccTenantsView` does, so both lists
 * agree after a write.
 *
 * All hooks use the tenant-scoped routes, the ones `PeoplePage.tsx` uses:
 *
 *   - `GET /v1/admin/members` — `services/api/src/modules/admin/routes.ts:411`
 *   - `GET /v1/admin/invitations` —
 *     `services/api/src/modules/identity/invitations.routes.ts:161`
 *   - `POST /v1/admin/invitations` — same file, `:166`
 *   - `POST /v1/admin/invitations/:id/resend` — same file, `:272`
 *   - `DELETE /v1/admin/invitations/:id` — same file, `:318`; soft revoke
 *     without a query, hard delete with `?confirm=hard` (E-GTCC-23-HARD1)
 *
 * The three writes of A-GTCC-CHECKS1 are the row-23 gap verbatim ("kein
 * Einladen, Erneut-Senden oder Widerrufen"). E-GTCC-23-HARD1 adds the fourth,
 * the hard delete, as its own hook: the soft revoke stays what it was
 * (`status → "revoked"`, row kept as the record of who was asked), the hard
 * one removes the row for good and is therefore gated in the view behind the
 * typed-identifier dialog gtcc1 also uses. What stays out on purpose:
 *
 *   - Member writes (suspend, re-role, remove, exit). Those are register row
 *     24 and a separate package; the writes card below still names them as
 *     absent.
 *   - The platform-owner variants (`/v1/admin/tenants/:tenantId/...`):
 *     `GtccTenantsView` already renders the per-tenant member and invitation
 *     cards from those, and duplicating them here would give the console two
 *     answers to the same question.
 *
 * Every write carries the same gate as the read it sits next to,
 * `tenant.members.write`
 * (`services/api/src/modules/identity/invitations.service.ts:58`), so no new
 * permission is introduced and a reader who already gets 403 on the list keeps
 * getting 403 here.
 */

export const GTCC_MEMBER_ROLES = ["owner", "admin", "dispatcher", "viewer"] as const;
export type GtccMemberRole = (typeof GTCC_MEMBER_ROLES)[number];

export const GTCC_MEMBER_STATUSES = ["active", "invited", "suspended"] as const;
export type GtccMemberStatus = (typeof GTCC_MEMBER_STATUSES)[number];

export const GTCC_INVITATION_STATUSES = ["pending", "accepted", "revoked", "expired"] as const;
export type GtccInvitationStatus = (typeof GTCC_INVITATION_STATUSES)[number];

export interface GtccMember {
  readonly id: string;
  readonly email: string;
  readonly displayName: string;
  readonly role: GtccMemberRole;
  readonly status: GtccMemberStatus;
  readonly lastActive: string | null;
  readonly createdAt: string;
}

/**
 * `total`, `byRole` and `byStatus` are counted over the whole tenant, while
 * `items` is cut off by `limit` — `services/api/src/modules/admin/members.repo.ts:246`
 * aggregates with `where tenantId = ...` and no limit at all. The two numbers
 * therefore answer different questions, and the view says which is which
 * instead of letting a truncated table pass for a headcount.
 */
export interface GtccMembersResponse {
  readonly items: readonly GtccMember[];
  readonly total: number;
  readonly byRole: Readonly<Record<GtccMemberRole, number>>;
  readonly byStatus: Readonly<Record<GtccMemberStatus, number>>;
}

export interface GtccInvitation {
  readonly id: string;
  readonly email: string;
  readonly role: GtccMemberRole;
  readonly status: GtccInvitationStatus;
  readonly displayName: string | null;
  readonly invitedByEmail: string;
  readonly tokenExpiresAt: string;
  readonly acceptedAt: string | null;
  readonly revokedAt: string | null;
  readonly createdAt: string;
}

export interface GtccInvitationsResponse {
  readonly items: readonly GtccInvitation[];
}

/** The page's own ceiling, and the API's: the querystring caps `limit` at 100. */
export const GTCC_MEMBER_LIMIT = 100;

export function useGtccMembers(): UseQueryResult<GtccMembersResponse, Error> {
  return useQuery<GtccMembersResponse, Error>({
    queryKey: ["gtcc", "ppl", "members", GTCC_MEMBER_LIMIT],
    queryFn: ({ signal }) =>
      apiGet<GtccMembersResponse>("/v1/admin/members", { limit: GTCC_MEMBER_LIMIT }, signal),
    staleTime: 30_000,
  });
}

export function useGtccInvitations(): UseQueryResult<GtccInvitationsResponse, Error> {
  return useQuery<GtccInvitationsResponse, Error>({
    queryKey: ["gtcc", "ppl", "invitations"],
    queryFn: ({ signal }) =>
      apiGet<GtccInvitationsResponse>("/v1/admin/invitations", undefined, signal),
    staleTime: 30_000,
  });
}

/** The body `POST /v1/admin/invitations` requires; `displayName` and `ttlDays` stay at the API default. */
export interface GtccInviteInput {
  readonly email: string;
  readonly role: GtccMemberRole;
}

/**
 * Both cache keys, always together: this page's own list and the gtcc1 list
 * next door. Same idiom as `GtccTenantsView`'s member card.
 */
function useInvalidateInvitations(): () => void {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["gtcc", "ppl", "invitations"] });
    void qc.invalidateQueries({ queryKey: ["admin", "invitations"] });
  };
}

export function useGtccCreateInvitation(): UseMutationResult<unknown, Error, GtccInviteInput> {
  const invalidate = useInvalidateInvitations();
  return useMutation<unknown, Error, GtccInviteInput>({
    mutationFn: (input) => apiSend("POST", "/v1/admin/invitations", input),
    onSuccess: () => invalidate(),
  });
}

export function useGtccResendInvitation(): UseMutationResult<unknown, Error, string> {
  const invalidate = useInvalidateInvitations();
  return useMutation<unknown, Error, string>({
    mutationFn: (id) => apiSend("POST", `/v1/admin/invitations/${id}/resend`),
    onSuccess: () => invalidate(),
  });
}

/**
 * Soft revoke. The route only accepts a `pending` row and answers 409
 * `invite_invalid` otherwise
 * (`services/api/src/modules/identity/invitations.routes.ts:318`); the view
 * mirrors that by offering the action on pending rows only, and the 409 stays
 * the authority.
 */
export function useGtccRevokeInvitation(): UseMutationResult<unknown, Error, string> {
  const invalidate = useInvalidateInvitations();
  return useMutation<unknown, Error, string>({
    mutationFn: (id) => apiSend("DELETE", `/v1/admin/invitations/${id}`),
    onSuccess: () => invalidate(),
  });
}

/**
 * Hard delete, E-GTCC-23-HARD1. Same route as the revoke, with `?confirm=hard`
 * (`services/api/src/modules/identity/invitations.routes.ts:310-345`): the
 * row is removed outright and the route answers 204 for any status, so the
 * view offers it on every row, not only on pending ones. 404 when the row is
 * already gone. The soft revoke above is untouched.
 */
export function useGtccHardDeleteInvitation(): UseMutationResult<void, Error, string> {
  const invalidate = useInvalidateInvitations();
  return useMutation<void, Error, string>({
    mutationFn: (id) => apiSend<void>("DELETE", `/v1/admin/invitations/${id}?confirm=hard`),
    onSuccess: () => invalidate(),
  });
}

// Placed after the invitation hooks above, whose exact line numbers are
// pinned by services/api/src/gtcc-view-endpoints-guarded.test.ts citations:
// inserting earlier in the file would shift those and break the pins.
import { toCappedListResult } from "@genius-tracker/api-client";

/**
 * `/v1/admin/members` caps `limit` server-side (max 100) and reports `total`
 * instead of a cursor, so the roster cannot be drained with `fetchAllPages`.
 * This gives it the same honest truncation signal via the shared
 * `@genius-tracker/api-client` helper instead of comparing `items.length`
 * and `total` ad hoc at each call site.
 */
export function gtccMembersTruncated(
  response: Pick<GtccMembersResponse, "items" | "total">,
): boolean {
  return toCappedListResult(response).truncated;
}
